import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { Campaign, CampaignStatus } from "../../../shared/types";

/** Operator lifecycle moves offered in the UI. Completing and archiving stay in their own flows. */
export type CampaignStatusAction = { to: CampaignStatus; label: string; pendingLabel: string; primary: boolean };

export function campaignStatusActions(status: CampaignStatus | string | null | undefined): CampaignStatusAction[] {
  if (status === "draft") return [{ to: "active", label: "Activate campaign", pendingLabel: "Activating…", primary: true }];
  if (status === "active") return [{ to: "paused", label: "Pause campaign", pendingLabel: "Pausing…", primary: false }];
  if (status === "paused") return [{ to: "active", label: "Resume campaign", pendingLabel: "Resuming…", primary: true }];
  return [];
}

/**
 * PATCH /api/campaigns/:id with a status. `expected_updated_at` makes a stale page fail with a
 * conflict instead of overwriting a change made elsewhere.
 */
export function useSetCampaignStatus(brandId: string | undefined, campaignId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { status: CampaignStatus; expected_updated_at?: string }) => {
      if (!brandId || !campaignId) throw new Error("Missing campaign context");
      const response = await api.patch<{ data: Campaign }>(`/api/campaigns/${encodeURIComponent(campaignId)}`, {
        brand_id: brandId, status: input.status, ...(input.expected_updated_at ? { expected_updated_at: input.expected_updated_at } : {}),
      });
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["campaigns", brandId, campaignId] });
      void qc.invalidateQueries({ queryKey: ["campaigns", brandId] });
    },
  });
}
